import axios from "axios";
import { cloudServiceBaseUrl, userServiceBaseUrl } from "../config";
import { computeAxiosInstance } from "./utils/computeAxiosInstance";
import { userInfoSessionStoreage } from "../utils/userInfoSessionStoreage";
import { authTokenPayload, isValidJsonString } from "../config/constant";

const userInfo = userInfoSessionStoreage.get();
const headers = {
  "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
  "x-bang-api-key": userInfo.apikey
};

const sheaders = {
  "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
  "x-bang-api-key": userInfo.apikey
};

const parseForApps = (apps, user) => {
  try {
    if (apps?.apps_account_info?.length > 0) {
      const allApps = apps.apps_account_info?.map((x) => JSON.parse(x.val));
      if (!user.apps) user.apps = [];
      user.apps = allApps.map((appToAdd) => {
        const existsInUser = user.apps.find((t) => t.appid === appToAdd.appid);
        if (existsInUser) {
          return existsInUser;
        } else {
          return appToAdd;
        }
      });
      return user;
    } else {
      return user;
    }
  } catch (err) {
    console.log(err);
    return user;
  }
};

const userService = axios.create({
  baseURL: `${
    typeof window === "object" ? window?.location?.protocol : "http:"
  }//${userServiceBaseUrl}`,
  headers
});

// Signup endpoints are unauthenticated. They must NOT inherit the api key that
// `headers` snapshotted out of sessionStorage at module load - with a leftover key
// present the user service authenticates that key instead of handling the signup,
// and fails with "user doesn't exist, authentication failed".
const publicUserService = axios.create({
  baseURL: `${
    typeof window === "object" ? window?.location?.protocol : "http:"
  }//${userServiceBaseUrl}`,
  headers: {
    "Content-Type": "application/json; charset=UTF-8"
  }
});

const provisionService = axios.create({
  baseURL: `https://${process.env.REACT_APP_PROVISION_API || "testprovision.bangdb.com:18080"}`,
  headers
});

const cloudService = axios.create({
  baseURL: `${
    typeof window === "object" ? window?.location?.protocol : "http:"
  }//${cloudServiceBaseUrl}`,
  sheaders
});

let beInstance = axios.create({});

export function updateInstances(serverDetails, apiKey) {
  beInstance = computeAxiosInstance(2, 4, serverDetails, apiKey);
}

if (typeof userInfo?.server_details?.[0] === "object") {
  updateInstances(userInfo.server_details, userInfo.apikey);
}

export const AppstoreApi = {};

AppstoreApi.getInstalledApps = async (payload) => {
  const { service, ...rest } = payload;
  if (service) {
    const protocol = `${typeof window === "object" ? window?.location?.protocol : "http:"}`;
    const res = await axios.post(`${protocol}//${service}/user/info`, {
      ...rest
    });
    const apps = await axios.post(`${protocol}//${service}/user/account_app_list`, {
      ...rest,
      appid: "*"
    });

    return parseForApps(apps.data, res.data);
  } else {
    const res = await userService.post("user/info", {
      ...rest
    });
    const apps = await userService.post("user/account_app_list", {
      ...rest,
      appid: "*"
    });
    const newuser = parseForApps(apps.data, res.data);
    return newuser;
  }
};

AppstoreApi.getAppDetail = async (appId) => {
  const res = await userService.post("/user/app/detail", {
    appid: appId
  });
  return res.data;
};

AppstoreApi.getAllApps = async (dbName) => {
  return userService
    .post(`/db/${dbName}/query`, {
      sql: `select * from _sys_app_details_tbl`
    })
    .then((res) => res.data);
};

AppstoreApi.putDocInStream = async (schemaName, streamName, payload) => {
  return beInstance.post(`/stream/${schemaName}/${streamName}`, payload).then((res) => res.data);
};

AppstoreApi.installApp = async (userid, apikey, appid, IP, PORT) => {
  let payload = {};
  if (IP && PORT) {
    payload = { appstore_us_ip: IP, appstore_us_port: PORT };
  }
  return beInstance
    .post("/db/app/install", {
      userid,
      apikey,
      appid,
      ...payload
    })
    .then((res) => res.data);
};

AppstoreApi.updateApp = async (payload = {}) => {
  return beInstance.post("/db/app/install", payload).then((res) => res.data);
};

AppstoreApi.usUpdateApp = async (apikey, payload = {}, us) => {
  if (us) {
    return axios
      .post(`https://${us}/db/app/install`, payload, {
        headers: {
          "x-bang-api-key": apikey
        }
      })
      .then((res) => res.data);
  } else {
    return beInstance.post("/db/app/install", payload).then((res) => res.data);
  }
};

AppstoreApi.uninstallApp = async (userid, apikey, appid) => {
  return beInstance
    .post("/db/app/uninstall", {
      userid,
      apikey,
      appid
    })
    .then((res) => res.data);
};

AppstoreApi.deleteApp = async (appId, adminId, adminPassword) => {
  return userService
    .post("/user/app/remove", {
      appid: appId,
      admin: adminId,
      admin_pwd: adminPassword,
      state: 0
    })
    .then((res) => res.data);
};

AppstoreApi.getAccountInfo = async (payload) => {
  return userService.post("/user/get_account_info", payload).then((res) => res.data);
};

AppstoreApi.updateAccountInfo = async (payload) => {
  const res = await userService.post("/user/add_account_info", payload);
  return res.data;
};

AppstoreApi.getReviews = async (payload) => {
  const res = await userService.post("/user/get_review", payload);
  return res.data;
};

AppstoreApi.addReview = async (payload) => {
  const res = await userService.post("/user/review", payload);
  return res.data;
};

AppstoreApi.getRazorPayPlan = async (payload) => {
  const res = await userService.post("/user/get_plan", payload);
  return res.data;
};

// userservice bucket download and upload

export const userServiceAddFileToBucket = async (
  accessKey,
  secretKey,
  bucketName,
  fileName,
  fileData,
  { onUploadProgress = () => {}, signal }
) => {
  try {
    // get file metadata
    const preUploadData = await userService({
      method: "post",
      url: `/brs/${bucketName}/putfile`,
      signal,
      data: JSON.stringify({
        access_key: accessKey,
        secret_key: secretKey,
        fsize: fileData.size,
        bucket_name: bucketName,
        key: fileName
      })
    }).then((res) => res.data);

    const { next_key, nslice, chunk_size } = preUploadData;

    let key = next_key;
    let progress = 0;
    let currentSlice = 0;

    const reader = new FileReader();
    reader.readAsArrayBuffer(fileData);

    reader.onload = async (e) => {
      const result = e.target.result;

      for (const _ of Array(nslice).keys()) {
        const chunk = result.slice(currentSlice * chunk_size, (currentSlice + 1) * chunk_size);

        let progressBeforeUpload = progress;

        const res = await userService({
          method: "post",
          url: `/brs/${bucketName}/putfile?key=${key}&nslice=${nslice}`,
          signal,
          data: chunk,
          onUploadProgress: (e) => {
            progress = progressBeforeUpload + (e.loaded / (e.total * nslice)) * 100;
            onUploadProgress(progress);
          },
          headers: {
            "Content-Type": "application/octet-stream",
            "x-bang-api-key": userInfo.apikey
          }
        });

        key = res.data.next_key;
        currentSlice++;
      }
    };
  } catch (err) {
    // check if axios error
    if (err.response) {
      const { errcode, msg } = err.response.data;
      switch (errcode) {
        case 409: {
          throw new Error(`${msg}`);
        }
        default:
          throw new Error(`${msg || "Error uploading file"}`);
      }
    } else {
      throw new Error(`${err.message || "Error uploading file"}`);
    }
  }
};

export const userServiceDownloadFileFromBucket = async (
  bucketName,
  fileName,
  accessKey,
  secretKey,
  { onDownloadProgress = () => {}, signal }
) => {
  const newInfo = isValidJsonString(sessionStorage.getItem("userInfo"))
    ? JSON.parse(sessionStorage.getItem("userInfo"))
    : null;
  const forumkey = isValidJsonString(sessionStorage.getItem("forum_admin_api_key"))
    ? JSON.parse(sessionStorage.getItem("forum_admin_api_key"))
    : "";

  // get file metadata
  const fileMeta = await userService({
    method: "post",
    url: `/brs/${bucketName}/getfile`,
    headers: {
      "x-bang-api-key": newInfo?.apikey || forumkey
    },
    signal,
    data: JSON.stringify({
      access_key: accessKey,
      secret_key: secretKey,
      bucket_name: bucketName,
      key: fileName
    })
  }).then((res) => res.data);

  const { nslice, next_key } = fileMeta;
  const fileNameFromResponse = next_key.split(":")[0];
  let key = next_key;
  let progress = 0;
  let currentSlice = 0;
  const blobs = [];

  for (let _ of Array(nslice).keys()) {
    if (currentSlice > 0) {
      key = `${fileNameFromResponse}:${currentSlice}`;
    }

    const progressBeforeDownload = progress;

    const res = await userService({
      method: "post",
      url: `/brs/${bucketName}/getfile?key=${key}&nslice=${nslice}`,
      signal,
      responseType: "arraybuffer",
      data: { nope: 1 },
      headers: {
        "content-type": "application/octet-stream",
        "x-bang-api-key": newInfo?.apikey || forumkey
      },
      onDownloadProgress: (e) => {
        progress = progressBeforeDownload + (e.loaded / (e.total * nslice)) * 100;
        onDownloadProgress(progress);
      }
    });

    if (res.data.msg) {
      throw new Error(`${res.data.msg}`);
    }

    currentSlice++;
    key = res.data.next_key;

    blobs.push(res.data);
  }

  return new File(blobs, fileName);
};

export const userServiceSearchFilesInBucket = async (bucketName, fileName) => {
  const newInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const modifiedFileName = fileName.trim() === "*" ? "*:*" : `${fileName.trim()}$%:*`;

  const res = await userService({
    method: "post",
    url: `/brs/object/list?key=${modifiedFileName}`,
    data: JSON.stringify({ bucket_name: bucketName }),
    headers: {
      "content-type": "text/plain",
      "x-bang-api-key": newInfo.apikey
    }
  });

  return res.data;
};

export const userServiceDeleteFileInBucket = async (bucketName, fileName) => {
  return await userService
    .post(
      `/brs/delete?key=${fileName}`,
      JSON.stringify({
        bucket_name: bucketName
      })
    )
    .then((res) => {
      // if success key exist and its value is 0 then delete was successful
      return res.data.hasOwnProperty("success") && res.data.success === 0;
    });
};

export async function getDbName() {
  const res = await userService.get("/db");
  return res.data.dbname;
}

export const updateAccount = async (json) => {
  const res = await userService.post("/user/update_account", json);
  return res.data;
};

export const login = async (userid, pwd) => {
  const res = await userService.post("/user/authenticate_user", {
    userid,
    pwd
  });
  return res.data;
};

export const authLogin = async (payload) => {
  const res = await userService.post("/user/authenticate_user", payload);
  return res.data;
};

export const changePassword = async (json) => {
  const res = await userService.post("/user/change_pwd", json);
  return res.data;
};

export const usChangePassword = (us, json) =>
  axios.post(`https://${us}:18080/user/change_pwd`, json, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
    }
  });

export const addSubscriptionTransaction = async (json) => {
  const res = await userService.post("/user/subscription_txn", json);
  return res.data;
};

export const addAccountOrder = async (json) => {
  const res = await userService.post("/user/account_order_op", json);
  return res.data;
};

export const getSubscriptionState = async (json) => {
  const res = await userService.post("/user/subscription_state", json);
  return res.data;
};

export const updateUser = async (json) => {
  const res = await userService.post("/user/update", json);
  return res.data;
};

export const getApiKey = async (json) => {
  const res = await userService.post("/user/get_api_key", json);
  return res.data;
};

export async function getUserInfo(userid, apiKey, service) {
  if (service) {
    const protocol = `${typeof window === "object" ? window?.location?.protocol : "http:"}`;
    const res = await axios.post(`${protocol}//${service}/user/info`, {
      userid,
      apikey: apiKey
    });
    const apps = await axios.post(`${protocol}//${service}/user/account_app_list`, {
      userid,
      apikey: apiKey,
      appid: "*"
    });

    return parseForApps(apps.data, res.data);
  } else {
    const res = await userService.post("/user/info", {
      userid,
      apikey: apiKey
    });
    const apps = await userService.post("/user/account_app_list", {
      userid,
      apikey: apiKey,
      appid: "*"
    });
    const newuser = parseForApps(apps.data, res.data);
    return newuser;
  }
}
export async function usGetUserInfo(us, payload) {
  us = `${us?.split(":")?.[0]}:18080`;
  const protocol = `${typeof window === "object" ? window?.location?.protocol : "http:"}`;
  const res = await axios.post(`${protocol}//${us}/user/info`, payload);
  const apps = await axios.post(`${protocol}//${us}/user/account_app_list`, {
    ...payload,
    appid: "*"
  });

  return parseForApps(apps.data, res.data);
}

export async function getUsers(payload) {
  const res = await userService.post("/user/get_users", payload);
  return res.data;
}

export const createUser = async (json) => {
  const res = await publicUserService.post("/user/create", json);
  return res.data;
};

export const createApiKey = async (json) => {
  const res = await publicUserService.post("/user/create_api_key", json);
  return res.data;
};

export const assignServers = async (payload) => {
  const res = await userService.post("/user/add_server_info", payload);
  return res.data;
};

// Provision service APIs

export const provisionSaasServer = async (payload) => {
  const newInfo = JSON.parse(sessionStorage.getItem("userInfo"));

  const res = await provisionService.post("/user/provision", payload, {
    headers: {
      "x-bang-api-key": newInfo?.apikey
    }
  });

  return res.data;
};

export const getProvisioningStatus = async (payload) => {
  const newInfo = JSON.parse(sessionStorage.getItem("userInfo"));

  const res = await provisionService.post("/user/provision_log", payload, {
    headers: {
      "x-bang-api-key": newInfo?.apikey
    }
  });

  return res.data;
};

// Cloud service APIs

export const createNewRazorPayOrder = async (payload) => {
  const res = await provisionService.post("/user/subscriptions", authTokenPayload(payload));
  return res.data;
};

export const getRazorPayKey = async () => {
  const res = await provisionService.post("/user/get_razorpay_key", authTokenPayload({}));
  return res.data;
};

export const getSubscription = async (payload) => {
  const res = await provisionService.post("/user/get_subscription", authTokenPayload(payload));
  return res.data;
};

export const addSupportIssue = async (payload) => {
  const getForumKey = async (json) => {
    const res = await axios.post(
      `https://${process.env.REACT_APP_SUPPORT_BASE_URL}/user/get_api_key`,
      json
    );

    return res.data;
  };

  // const forumkey = sessionStorage.getItem("forum_admin_api_key")
  //   ? JSON.parse(sessionStorage.getItem("forum_admin_api_key"))
  //   : "";
  const getSupportApiKey = async (pld) => {
    const resp = await axios.post(
      `https://${
        process.env.REACT_APP_SUPPORT_BASE_URL || "testbe.bangdb.com:18080"
      }/user/get_api_key`,
      pld
    );
    return resp.data;
  };

  const pload = authTokenPayload({ userid: "forum_admin" });
  const resp = await getSupportApiKey(pload);
  const job = await authTokenPayload(payload);
  const res = await axios.post(
    `https://${
      process.env.REACT_APP_SUPPORT_BASE_URL || "testbe.bangdb.com:18080"
    }/db/mydb/work/put_work`,
    job,
    {
      headers: {
        "x-bang-api-key": resp.apikey,
        "x-bang-api-src": "opaque"
      }
    }
  );
  return res.data;
};

export const updateAppsInfo = async (payload, us) => {
  if (us) {
    const res = await axios.post(`https://${us}/user/update_apps_info`, payload, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        // "x-bang-api-key": payload?.apikey,
      }
    });
  } else {
    const res = userService.post(`/user/update_apps_info`, payload, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
      }
    });
    return res.data;
  }
};

export const deleteAppsInfo = async (payload, us) => {
  if (us) {
    const res = await axios.post(`https://${us}/user/delete_apps_info`, payload, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
      }
    });

    return res.data;
  } else {
    const res = userService.post(`/user/delete_apps_info`, payload, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
      }
    });

    return res.data;
  }
};
